import { cn } from 'cn';

import type { GetProjectQuery } from '@/survey-graphql';

import SurveyAiFormsAiSectionMobile from './survey-ai-forms-ai-section-mobile';
import SurveyAiFormsFormSectionMobile from './survey-ai-forms-form-section-mobile';
import type {
  FormAnswerValue,
  ResponseMode,
  ViewMode,
} from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface Props {
  project: NonNullable<GetProjectQuery['project']>;
  currentQuestion: ProjectQuestion | null;
  currentQuestionId: string | null;
  currentQuestionIndex: number;
  totalQuestions: number;
  responseMode: ResponseMode;
  viewMode: ViewMode;
  progress: number;
  estimatedTotalTimeInSeconds: number;
  onResponseSubmit: (value: FormAnswerValue) => void;
  onQuestionFocus: (questionId: string) => void;
  onQuestionNavigate: (questionId: string) => void;
  saveTranscript: (questionId: string, transcript: string) => void;
  getTranscript: (questionId: string) => string;
  onViewModeToggle?: () => void;
  onSubmit: () => void;
  onFormSubmit: () => void;
  registerVoiceStopCallback: (callback: (() => void) | null) => void;
  className?: string;
  isProcessingResponse?: boolean;
  isSubmitting?: boolean;
  canSubmit: boolean;
  submittedQuestionIds: Set<string>;
  validationErrors: Set<string>;
  hasTriedSubmit: boolean;
  isPreviewMode?: boolean;
  isAIFormAssistanceEnabled: boolean;
}

export default function SurveyAiFormsMobileLayout({
  project,
  currentQuestion,
  currentQuestionId,
  currentQuestionIndex,
  totalQuestions,
  responseMode,
  viewMode,
  progress,
  estimatedTotalTimeInSeconds,
  onResponseSubmit,
  onQuestionFocus,
  onQuestionNavigate,
  saveTranscript,
  getTranscript,
  onViewModeToggle,
  onSubmit,
  onFormSubmit,
  registerVoiceStopCallback,
  className = '',
  isProcessingResponse = false,
  isSubmitting = false,
  canSubmit,
  submittedQuestionIds,
  validationErrors,
  hasTriedSubmit,
  isPreviewMode = false,
  isAIFormAssistanceEnabled,
}: Props) {
  const isFormMode = viewMode === 'form' || !isAIFormAssistanceEnabled;

  return (
    <div
      data-test='mobile-layout'
      className={cn('h-screen w-full overflow-hidden', className)}
    >
      {isFormMode ? (
        <div className='size-full scrollbar-none overflow-y-auto [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
          <SurveyAiFormsFormSectionMobile
            questions={project.questions}
            currentQuestionId={currentQuestionId}
            onQuestionFocus={onQuestionFocus}
            onSubmit={onFormSubmit}
            onViewModeToggle={onViewModeToggle}
            progress={progress}
            estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
            viewMode='form'
            isSubmitting={isSubmitting}
            submittedQuestionIds={submittedQuestionIds}
            canSubmit={canSubmit}
            validationErrors={validationErrors}
            hasTriedSubmit={hasTriedSubmit}
            isPreviewMode={isPreviewMode}
            isAIFormAssistanceEnabled={isAIFormAssistanceEnabled}
          />
        </div>
      ) : (
        <div className='size-full scrollbar-none overflow-y-auto [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
          <SurveyAiFormsAiSectionMobile
            project={project}
            currentQuestion={currentQuestion}
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={totalQuestions}
            responseMode={responseMode}
            viewMode={viewMode}
            progress={progress}
            estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
            onResponseSubmit={onResponseSubmit}
            onQuestionFocus={onQuestionNavigate}
            saveTranscript={saveTranscript}
            getTranscript={getTranscript}
            onViewModeToggle={onViewModeToggle}
            onSubmit={onSubmit}
            registerVoiceStopCallback={registerVoiceStopCallback}
            isProcessingResponse={isProcessingResponse}
            isSubmitting={isSubmitting}
            canSubmit={canSubmit}
            isUserVoiceEnabled={project.isUserVoiceEnabled ?? false}
            isPreviewMode={isPreviewMode}
            isAIFormAssistanceEnabled={isAIFormAssistanceEnabled}
          />
        </div>
      )}
    </div>
  );
}
